import React, { useEffect, useRef, useState } from "react";
import { Chart, registerables } from "chart.js";
import { useNavigate } from "react-router-dom";
import { ShippingData } from "../../types";

interface Stat {
    icon: string;
    link: string;
    label: string;
    bgColor: string;
    key: string;
}

interface DashboardProps {
    startDate: string;
    endDate: string;
    dashboardData: any;
    delayedList: ShippingData[];
}

Chart.register(...registerables);

const DeliveryDashboard = (props: DashboardProps) => {
    const navigate = useNavigate();

    const barChartRef = useRef<HTMLCanvasElement>(null);
    const barChartInstance = useRef<Chart | null>(null);
    const [chartData, setChartData] = useState([0, 0, 0, 0]);

    const [barChartData, setBarChartData] = useState([0, 0, 0, 0, 0, 0, 0, 0]);
    const [dropShiopBarchartData, setDropshipBarchartData] = useState([
        0, 0, 0, 0, 0, 0, 0, 0,
    ]);
    const [inventoryBarchartData, setInventoryBarchartData] = useState([
        0, 0, 0, 0, 0, 0, 0, 0,
    ]);
    const [delayedBarChartData, setDelayedBarChartData] = useState([
        0, 0, 0, 0, 0, 0, 0, 0,
    ]);

    function getLabels() {
        // Get month names for labels
        const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];

        // Get current and previous month names
        const currentMonthIndex = new Date().getMonth(); // 0-indexed
        const currentMonthName = monthNames[currentMonthIndex];

        // Calculate previous month
        let previousMonthIndex = currentMonthIndex - 1;
        if (previousMonthIndex < 0) {
            previousMonthIndex = 11; // December
        }
        const previousMonthName = monthNames[previousMonthIndex];

        // Generate dynamic labels
        const labels = [
            `${previousMonthName} W1`,
            `${previousMonthName} W2`,
            `${previousMonthName} W3`,
            `${previousMonthName} W4`,
            `${currentMonthName} W1`,
            `${currentMonthName} W2`,
            `${currentMonthName} W3`,
            `${currentMonthName} W4`,
        ];

        return labels;
    }

    useEffect(() => {
        if (!barChartRef.current) return;

        const barCtx = barChartRef.current.getContext("2d");
        if (!barCtx) return;

        if (barChartInstance.current) {
            barChartInstance.current.destroy();
        }

        barChartInstance.current = new Chart(barCtx, {
            type: "line",
            data: {
                labels: getLabels(),
                datasets: [
                    {
                        label: "Total Amount",
                        data: barChartData, // ✅ correct data
                        borderColor: "rgb(59, 130, 246)",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                        fill: true,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
            },
        });

        return () => {
            barChartInstance.current?.destroy();
        };
    }, [barChartData]); // ✅ FIX

    useEffect(() => {
        setBarChartData([
            props.dashboardData.quarter?.overview.q1.totalAmount,
            props.dashboardData.quarter?.overview.q2.totalAmount,
            props.dashboardData.quarter?.overview.q3.totalAmount,
            props.dashboardData.quarter?.overview.q4.totalAmount,
            props.dashboardData.quarter?.overview.q5.totalAmount,
            props.dashboardData.quarter?.overview.q6.totalAmount,
            props.dashboardData.quarter?.overview.q7.totalAmount,
            props.dashboardData.quarter?.overview.q8.totalAmount,
        ]);
    }, [props.dashboardData]);

    function isDateOlderThanToday(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return inputDate < today;
    }

    function isDateInTwoDays(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();
        const twoDaysFromNow = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        twoDaysFromNow.setHours(0, 0, 0, 0);
        twoDaysFromNow.setDate(today.getDate() + 2);

        return inputDate.getTime() === twoDaysFromNow.getTime();
    }

    function getNextStepColor(date: string | undefined) {
        if (!date) {
            return "#fff";
        }

        if (isDateOlderThanToday(date)) {
            return "#fa7f52ff";
        } else if (isDateInTwoDays(date)) {
            return "#e0f052ff";
        } else {
            return "#43bd38ff";
        }
    }

    function truncateText(text: string | null, maxLength: number = 20): string {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    }

    function compareDateToToday(inputDateStr: string | undefined) {
        if (!inputDateStr) return "";

        // Normalize input: handle both "YYYY-MM-DD" and "YYYY-MM-DD HH:mm:ss"
        const normalized =
            inputDateStr.length === 10
                ? inputDateStr + " 00:00:00"
                : inputDateStr;

        const inputDate = new Date(normalized.replace(" ", "T"));
        const today = new Date();

        // Normalize today's time to midnight
        today.setHours(0, 0, 0, 0);

        // Calculate difference in days
        const diffMs = inputDate.getTime() - today.getTime();
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

        // Format with + or -
        if (diffDays === 0) return "0 days";

        const sign = diffDays > 0 ? "+" : "-";
        return `${sign}${Math.abs(diffDays)} day${
            Math.abs(diffDays) > 1 ? "s" : ""
        }`;
    }

    return (
        <div
            style={{
                fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                backgroundColor: "#f8f9fa",
                padding: "20px",
            }}
        >
            {/* Main Content */}
            <div className="row h-100" style={{ backgroundColor: "#fff" }}>
                {/* Bar */}
                <div className="col-7">
                    <div className="card border-0 h-100">
                        <div className="card-body p-3">
                            <div
                                style={{
                                    height: "400px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <canvas ref={barChartRef}></canvas>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-5">
                    <div className="card border-0">
                        <div className="card-body p-3">
                            <div
                                className="card border-0 rounded-3"
                                style={{
                                    backgroundColor: "#bbddf1ff",
                                    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                                }}
                            >
                                {/* Header */}
                                <div
                                    className="d-flex align-items-between px-3 py-4"
                                    style={{
                                        justifyContent: "space-between",
                                    }}
                                >
                                    {/* LEFT: Label + Icon */}
                                    <div className="d-flex align-items-center gap-2">
                                        <h6
                                            className="fw-semibold mb-0"
                                            style={{ fontSize: "0.85rem" }}
                                        >
                                            Overview
                                        </h6>
                                        <img
                                            src="/icons/dashboard/docs_waiting.png"
                                            alt="docs-waiting"
                                            style={{ width: 16, height: 16 }}
                                        />
                                    </div>
                                    <h6
                                        className="fw-semibold mb-0"
                                        style={{ fontSize: "0.85rem" }}
                                    >
                                        Total:{" "}
                                        {props.dashboardData.overview.totalCount.toLocaleString()}{" "}
                                        | AED{" "}
                                        {props.dashboardData.overview.totalAmount.toLocaleString()}{" "}
                                        | SO{" "}
                                        {props.dashboardData.overview.totalSo.toLocaleString()}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row px-3">
                        <div className="col-6">
                            <div
                                className="card border-0 rounded-3"
                                style={{
                                    backgroundColor: "#9af8ecff",
                                    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                                }}
                            >
                                <div className="card-body p-3">
                                    <h6
                                        className="fw-semibold mb-0"
                                        style={{ fontSize: "0.85rem" }}
                                    >
                                        Total Counts Shipped:{" "}
                                        {props.dashboardData.overview.totalCountShipped.toLocaleString()}{" "}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div
                                className="card border-0 rounded-3"
                                style={{
                                    backgroundColor: "#a7dfb0ff",
                                    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                                }}
                            >
                                <div className="card-body p-3">
                                    <h6
                                        className="fw-semibold mb-0"
                                        style={{ fontSize: "0.85rem" }}
                                    >
                                        Total Counts Delivered:{" "}
                                        {props.dashboardData.overview.totalCountDelivered.toLocaleString()}{" "}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeliveryDashboard;
